/**
 * Constantes partagées entre client et serveur pour les disponibilités
 * Source unique de vérité - ne pas dupliquer ces valeurs ailleurs
 */

// Raisons de blocage d'une date
export const BLOCKED_DATE_REASONS = ['BOOKED', 'MAINTENANCE', 'OTHER'] as const
export type BlockedDateReason = (typeof BLOCKED_DATE_REASONS)[number]

// Configuration des raisons avec labels et métadonnées
export const BLOCKED_DATE_REASON_CONFIG = {
  BOOKED: {
    label: 'Réservé',
    description: 'Chambre réservée par un voyageur',
    color: 'error',
    icon: 'i-lucide-calendar-check',
  },
  MAINTENANCE: {
    label: 'Maintenance',
    description: 'Chambre en travaux ou indisponible temporairement',
    color: 'warning',
    icon: 'i-lucide-wrench',
  },
  OTHER: {
    label: 'Autre',
    description: 'Autre raison d\'indisponibilité',
    color: 'neutral',
    icon: 'i-lucide-calendar-off',
  },
} as const

// Tokens sémantiques V1.1 — source de vérité pour les composants availability
export const AVAILABILITY_TOKENS = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    icon: 'i-lucide-check',
    iconColor: 'text-green-700',
    border: 'border-green-300',
    label: 'common.availability.legend.available',
  },
  blocked: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    icon: 'i-lucide-lock',
    iconColor: 'text-red-700',
    border: 'border-red-300',
    label: 'common.availability.legend.blocked',
  },
  selecting: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    icon: 'i-lucide-pointer',
    iconColor: 'text-blue-700',
    border: 'border-blue-300',
    label: 'common.availability.legend.selecting',
  },
  today: {
    ring: 'ring-2 ring-primary-500',
    label: 'availability.legend.today',
  },
} as const

/**
 * Forme d'un jeu de tokens de cellule consommable par `AvailabilityCalendarGrid`.
 * Seuls `available` et `blocked` sont surchargeables : `selecting` et `today`
 * appartiennent au langage d'interaction owner, absent des surfaces publiques.
 *
 * Pas de `label` ici : les surfaces publiques portent leurs propres clés i18n
 * (`gite.public.calendar.*`, `common.availability.legend*`), et un libellé
 * recopié dans les tokens n'était consommé par aucun composant.
 */
interface AvailabilityCellStateTokens {
  readonly bg: string
  readonly text: string
  readonly icon: string
  readonly iconColor: string
  readonly border: string
}

export interface AvailabilityCellTokens {
  readonly available: AvailabilityCellStateTokens
  readonly blocked: AvailabilityCellStateTokens
}

/**
 * Variante PUBLIQUE des tokens de cellule (4.74.4).
 *
 * ATTENTION — `green` n'est PAS le vert de Tailwind dans ce dépôt : `main.css`
 * réécrit tout son ramp en sauge de marque, tandis qu'`emerald`, `amber` et
 * `red` gardent les teintes Tailwind vives. Ce jeu public est donc délibérément
 * en `emerald`, pour parler la même langue que le calendrier public chambres
 * d'hôtes (`holi/InteractiveCalendar.vue`), déjà en emerald. Arbitrage tranché
 * en 4.74.4 : parité entre les deux fiches voyageur.
 *
 * Contrastes texte/fond mesurés : emerald-800 sur emerald-100 ≈ 6,9:1,
 * red-800 sur red-100 ≈ 6,8:1 — au-dessus du seuil AA de 4,5:1.
 *
 * `AVAILABILITY_TOKENS` reste le défaut de la grille : les calendriers
 * owner/admin ne bougent pas.
 */
export const AVAILABILITY_PUBLIC_TOKENS = {
  available: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    icon: 'i-lucide-check',
    iconColor: 'text-emerald-800',
    border: 'border-emerald-300',
  },
  blocked: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    icon: 'i-lucide-lock',
    iconColor: 'text-red-800',
    border: 'border-red-300',
  },
} as const satisfies AvailabilityCellTokens

// Nombre de mois navigables en avant dans les calendriers de disponibilité
export const CALENDAR_MONTHS_AHEAD = 18

// Couleurs pour le calendrier (V1 - conservé pour compatibilité)
export const CALENDAR_COLORS = {
  available: 'bg-green-100 hover:bg-green-200 text-green-800',
  blocked: 'bg-red-100 hover:bg-red-200 text-red-800',
  blockedMaintenance: 'bg-amber-100 hover:bg-amber-200 text-amber-800',
  blockedOther: 'bg-neutral-100 hover:bg-neutral-200 text-neutral-600',
  today: 'ring-2 ring-primary-500',
  selected: 'ring-2 ring-primary-500 bg-primary-100',
  past: 'opacity-50 cursor-not-allowed',
  // Périodes tarifaires (optionnel)
  highSeason: 'border-b-2 border-warning-500',
  lowSeason: 'border-b-2 border-info-500',
} as const

// Helpers pour les mois (français)
export const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
] as const

// Jours de la semaine (français) - commence par lundi
export const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const

// Jours complets
export const DAYS_FULL_FR = [
  'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche',
] as const

/**
 * Normalise une date en retirant l'heure (00:00:00 UTC)
 * @param date - Date à normaliser
 * @returns Date normalisée à minuit UTC
 */
export function normalizeDateToUTC(date: Date): Date {
  const normalized = new Date(date)
  normalized.setUTCHours(0, 0, 0, 0)
  return normalized
}

/**
 * Convertit une date en clé de chaîne pour la comparaison
 * @param date - Date à convertir
 * @returns Chaîne au format YYYY-MM-DD
 */
export function dateToKey(date: Date): string {
  const isoStr = date.toISOString().split('T')[0]
  return isoStr ?? ''
}

/**
 * Génère un tableau de dates entre deux dates (inclus)
 * @param startDate - Date de début
 * @param endDate - Date de fin
 * @returns Tableau de dates
 */
export function generateDateRange(startDate: Date, endDate: Date): Date[] {
  const dates: Date[] = []
  const current = normalizeDateToUTC(new Date(startDate))
  const end = normalizeDateToUTC(new Date(endDate))

  while (current <= end) {
    dates.push(new Date(current))
    current.setUTCDate(current.getUTCDate() + 1)
  }

  return dates
}
